import { useEffect, useState } from 'react';
import { formatCurrency } from '../../utils/formatting';
import { fetchDashboard, fetchSettings, saveSettings } from '../../utils/insuranceApi';
import { useHouseholdDetail } from '../../context/HouseholdDetailContext';

export default function InsuranceDashboard({ refreshKey, selectedState, onSelectState }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGap, setShowGap] = useState(false);
  const [showStateEditor, setShowStateEditor] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const { openHousehold } = useHouseholdDetail();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchDashboard()
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [refreshKey, reloadTick]);

  if (loading) return null; // Silent loading - dashboard is auxiliary
  if (error || !data) return null;

  const k = data.kpis || {};
  const gapHouseholds = data.gap_households || [];
  const stateBreakdown = data.state_breakdown || [];
  const licensedStates = data.licensed_states || { p_c: [], life_health: [] };

  return (
    <div className="space-y-3">
      {/* KPI strip */}
      <div className="grid grid-cols-5 gap-3">
        <KPI label="In Force" value={k.in_force_count} accent="deep-teal"
             sub={`${k.households_covered} households · ${formatCurrency(k.annual_premium_in_force)}/yr`} />
        <KPI label="We Broker" value={k.we_broker_count} accent="bullogic-blue"
             sub={`${formatCurrency(k.annual_premium_we_broker)}/yr commission base`} />
        <KPI label="Future Effective" value={k.future_effective_count} accent="amber-500"
             sub={k.future_effective_count > 0 ? 'Action: shop before effective date' : 'None pending'} />
        <KPI label="Expiring 30 days" value={k.expiring_30_days} accent={k.expiring_30_days > 0 ? 'rose' : 'gray-400'}
             sub={k.expiring_30_days > 0 ? 'Renewal tasks active' : 'Nothing imminent'} />
        <KPI label="Coverage Gaps" value={k.households_with_gap} accent={k.households_with_gap > 0 ? 'rose' : 'deep-teal'}
             sub={`${k.households_with_gap} household${k.households_with_gap !== 1 ? 's' : ''} with no active policy`}
             clickable={k.households_with_gap > 0}
             onClick={() => setShowGap(s => !s)} />
      </div>

      {/* Gap report - households missing coverage */}
      {showGap && gapHouseholds.length > 0 && (
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-[12px] font-bold text-navy">Households Without Active Insurance ({gapHouseholds.length})</h3>
              <p className="text-[10px] text-[#888]">Reach out to these clients to get current policy docs uploaded.</p>
            </div>
            <button
              onClick={() => setShowGap(false)}
              className="text-[10px] font-bold text-[#888] hover:text-navy"
            >
              Hide
            </button>
          </div>
          <div className="grid grid-cols-3 gap-x-6 gap-y-1 text-[12px]">
            {gapHouseholds.map(h => (
              <div key={h.household_id} className="flex items-center justify-between">
                <button
                  onClick={() => openHousehold(h.household_id)}
                  className="text-navy hover:text-bullogic-blue hover:underline transition-colors text-left"
                  title="Open household card"
                >
                  {h.household_name}
                </button>
                <span className="text-[10px] text-[#aaa] ml-2">
                  {!h.has_any_policy ? 'No docs' : `${h.expired_policies > 0 ? `${h.expired_policies} exp` : ''}${h.expired_policies > 0 && h.future_policies > 0 ? ', ' : ''}${h.future_policies > 0 ? `${h.future_policies} future` : ''}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-state breakdown */}
      {stateBreakdown.length > 0 && (
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-[12px] font-bold text-navy">In-Force Policies by State</h3>
              <p className="text-[10px] text-[#888]">
                P&amp;C licensed: {licensedStates.p_c?.join(', ') || '(none)'} · Life/Health licensed: {licensedStates.life_health?.join(', ') || '(none)'}
              </p>
            </div>
            <button
              onClick={() => setShowStateEditor(s => !s)}
              className="text-[10px] font-bold text-bullogic-blue hover:text-navy"
            >
              {showStateEditor ? 'Close' : 'Manage Licensed States'}
            </button>
          </div>

          {showStateEditor && (
            <LicensedStatesEditor
              initial={licensedStates}
              onSaved={() => { setShowStateEditor(false); setReloadTick(t => t + 1); }}
            />
          )}

          <div className="grid grid-cols-3 gap-2">
            {stateBreakdown.map(s => {
              const pcGap = s.pc_count > 0 && !s.pc_licensed;
              const lhGap = s.lh_count > 0 && !s.lh_licensed;
              const anyGap = pcGap || lhGap;
              const isSelected = selectedState === s.state;
              return (
                <div
                  key={s.state}
                  onClick={() => onSelectState?.(s.state)}
                  className={`rounded-[3px] p-2.5 border cursor-pointer transition-all ${
                    isSelected
                      ? 'border-bullogic-blue bg-bullogic-blue/10 ring-2 ring-bullogic-blue/30'
                      : anyGap
                        ? 'border-rose/30 bg-rose/5 hover:border-rose/60'
                        : 'border-gray-200 bg-bone/30 hover:border-bullogic-blue/40 hover:bg-bone/60'
                  }`}
                  title={isSelected ? 'Click to clear filter' : `Click to filter to ${s.state}`}
                >
                  <div className="flex items-baseline justify-between">
                    <div className="text-[14px] font-bold text-navy">{s.state}</div>
                    <div className="text-[10px] text-[#888]">{s.policy_count} polic{s.policy_count === 1 ? 'y' : 'ies'} · {s.household_count} hh</div>
                  </div>
                  <div className="text-[10px] text-[#666] mt-1">{formatCurrency(s.annual_premium)}/yr</div>
                  <div className="flex gap-1 mt-1.5 flex-wrap">
                    {s.pc_count > 0 && (
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-[2px] ${
                        s.pc_licensed ? 'bg-deep-teal/15 text-deep-teal' : 'bg-rose/15 text-rose'
                      }`}>
                        P&amp;C {s.pc_count}{!s.pc_licensed && ' · NOT LICENSED'}
                      </span>
                    )}
                    {s.lh_count > 0 && (
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-[2px] ${
                        s.lh_licensed ? 'bg-deep-teal/15 text-deep-teal' : 'bg-rose/15 text-rose'
                      }`}>
                        L/H {s.lh_count}{!s.lh_licensed && ' · NOT LICENSED'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function LicensedStatesEditor({ initial, onSaved }) {
  const [pcText, setPcText] = useState((initial.p_c || []).join(', '));
  const [lhText, setLhText] = useState((initial.life_health || []).join(', '));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  function parse(text) {
    return text.split(/[,\s]+/).map(s => s.trim().toUpperCase()).filter(s => /^[A-Z]{2}$/.test(s));
  }

  async function handleSave() {
    setSaving(true);
    setErr(null);
    try {
      await saveSettings({
        licensed_states: { p_c: parse(pcText), life_health: parse(lhText) },
      });
      onSaved?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mb-4 p-3 bg-bone/40 rounded-[3px] border border-gray-200 space-y-2">
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[#888]">Property &amp; Casualty</label>
        <input
          value={pcText}
          onChange={e => setPcText(e.target.value)}
          placeholder="TX, PA"
          className="w-full mt-1 px-2 py-1.5 text-[12px] bg-white border border-gray-300 rounded-[3px] uppercase tracking-wider"
        />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[#888]">Life &amp; Health</label>
        <input
          value={lhText}
          onChange={e => setLhText(e.target.value)}
          placeholder="TX, PA, CA"
          className="w-full mt-1 px-2 py-1.5 text-[12px] bg-white border border-gray-300 rounded-[3px] uppercase tracking-wider"
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        {err && <span className="text-[10px] text-rose mr-2">{err}</span>}
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-3 py-1 text-[11px] font-bold rounded-[3px] ${
            saving ? 'bg-gray-200 text-gray-400' : 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90'
          }`}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, accent = 'navy', clickable, onClick }) {
  // Tailwind needs literal class names to compile - use a small map
  const accentClass = {
    'deep-teal': 'text-deep-teal',
    'bullogic-blue': 'text-bullogic-blue',
    'rose': 'text-rose',
    'amber-500': 'text-amber-500',
    'gray-400': 'text-gray-400',
    'navy': 'text-navy',
  }[accent] || 'text-navy';
  return (
    <div
      onClick={clickable ? onClick : undefined}
      className={`bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-3 ${clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    >
      <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#888] mb-0.5">{label}</div>
      <div className={`text-[24px] font-bold leading-tight ${accentClass}`}>{value ?? '0'}</div>
      <div className="text-[10px] text-[#888] mt-0.5">{sub}</div>
    </div>
  );
}
